import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, TextField } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { EMOJI_CATEGORIES } from './constants';

type Props = {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  t: any;
};

export default function EmojiPickerCustom({ onSelect, onClose, anchorRef, t }: Props) {
  const [search, setSearch] = useState('');
  const [catBarIdx, setCatBarIdx] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [catBarFocus, setCatBarFocus] = useState(false);
  const cat = EMOJI_CATEGORIES[catBarIdx];
  const emojis = cat.emojis.filter(e => {
    if (!search) return true;
    const term = search.toLowerCase();
    return t(`whatsapp.emoji.names.${e.nameKey}`).toLowerCase().includes(term);
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedIdx(0);
  }, [catBarIdx, search]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorRef, onClose]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", "Enter", "Tab"].includes(e.key)) {
        e.preventDefault();
      }

      if (catBarFocus) {
        if (e.key === 'ArrowRight') {
          setCatBarIdx(idx => (idx + 1) % EMOJI_CATEGORIES.length);
        } else if (e.key === 'ArrowLeft') {
          setCatBarIdx(idx => (idx - 1 + EMOJI_CATEGORIES.length) % EMOJI_CATEGORIES.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          setCatBarFocus(false);
        }
      } else {
        if (!emojis.length) return;
        const perRow = 8;
        if (e.key === 'ArrowRight') {
          setSelectedIdx(idx => (idx + 1) % emojis.length);
        } else if (e.key === 'ArrowLeft') {
          setSelectedIdx(idx => (idx - 1 + emojis.length) % emojis.length);
        } else if (e.key === 'ArrowDown') {
          setSelectedIdx(idx => (idx + perRow < emojis.length ? idx + perRow : idx));
        } else if (e.key === 'ArrowUp') {
          setSelectedIdx(idx => (idx - perRow >= 0 ? idx - perRow : idx));
        } else if (e.key === 'Enter') {
          onSelect(emojis[selectedIdx].char);
        } else if (e.key === 'Tab') {
          setCatBarFocus(true);
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [catBarFocus, emojis, selectedIdx, onSelect, catBarIdx]);

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        position: 'absolute', 
        bottom: '50px', 
        right: 0, 
        zIndex: 10, 
        width: 340, 
        bgcolor: 'background.paper', 
        border: '1px solid #ccc', 
        borderRadius: 2, 
        p: 2, 
        boxShadow: 3,
        outline: catBarFocus ? '2px solid #1976d2' : 'none'
      }}
      tabIndex={0}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <Typography variant="subtitle1" flex={1}>{t('whatsapp.emoji.selectEmoji')}</Typography>
        <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
      </Box>
      <Box
        display="flex"
        gap={1}
        mb={1}
        sx={{ overflowX: 'auto', pb: 1 }}
      >
        {EMOJI_CATEGORIES.map((cat, idx) => (
          <IconButton
            key={cat.key}
            size="small"
            onClick={() => { setCatBarIdx(idx); setCatBarFocus(false); }}
            color={catBarIdx === idx ? 'primary' : 'default'}
            sx={{ border: catBarFocus && catBarIdx === idx ? '2px solid #1976d2' : 'none' }}
          >
            {cat.icon}
          </IconButton>
        ))}
      </Box>
      <TextField
        size="small"
        fullWidth
        placeholder={t('whatsapp.emoji.searchPlaceholder')}
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 1 }}
        autoFocus
      />
      <Box
        sx={{ 
          maxHeight: 200, 
          overflowY: 'auto', 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1
        }}
      >
        {emojis.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('whatsapp.noMessagesFound')}</Typography>
        ) : emojis.map((emoji, idx) => (
          <Box
            key={emoji.char}
            component="span"
            sx={{
              fontSize: 24,
              cursor: 'pointer',
              p: 0.5,
              borderRadius: 1,
              bgcolor: idx === selectedIdx ? 'primary.light' : 'transparent',
              color: idx === selectedIdx ? 'primary.main' : 'inherit',
              boxShadow: idx === selectedIdx ? 2 : 0,
            }}
            onClick={() => onSelect(emoji.char)}
            title={t(`whatsapp.emoji.names.${emoji.nameKey}`)}
          >
            {emoji.char}
          </Box>
        ))}
      </Box>
    </Box>
  );
} 
